import { Box, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { chartFadeSx, chartTheme, LABEL_COLOR } from '../constants';
import type { SeriesFilter, UserBarRow } from '../types';
import { barKeysFor, seriesColorsFor } from '../utils';

export const MetricsUserBarChart = ({
    data,
    seriesFilter,
}: {
    data: UserBarRow[];
    seriesFilter: SeriesFilter;
}) => {
    if (data.length === 0) {
        return (
            <Box
                sx={{
                    height: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography sx={{ color: LABEL_COLOR }}>No quote activity in this period</Typography>
            </Box>
        );
    }

    const keys = barKeysFor(seriesFilter);
    const maxValue = Math.max(
        0,
        ...data.flatMap(row => keys.map(key => Number(row[key as keyof UserBarRow] ?? 0)))
    );
    // Integer-only ticks on the value axis (avoids 0.5, 1.5, … for small counts).
    const tickStep = Math.max(1, Math.ceil(maxValue / 5));
    const tickValues: number[] = [];
    for (let v = 0; v <= maxValue; v += tickStep) {
        tickValues.push(v);
    }
    if (tickValues[tickValues.length - 1] !== maxValue && maxValue > 0) {
        tickValues.push(maxValue);
    }

    return (
        <Box sx={{ height: Math.max(320, data.length * 44), ...chartFadeSx }}>
            <ResponsiveBar
                data={data}
                keys={keys}
                indexBy="user"
                groupMode="grouped"
                layout="horizontal"
                margin={{ top: 28, right: 28, bottom: 40, left: 120 }}
                padding={0.35}
                innerPadding={4}
                colors={seriesColorsFor(seriesFilter)}
                borderRadius={8}
                enableLabel={data.length <= 12}
                labelSkipWidth={16}
                valueFormat={v => `${Math.round(Number(v))}`}
                label={d => `${Math.round(Number(d.value))}`}
                labelTextColor="#fff"
                axisBottom={{
                    tickSize: 0,
                    tickPadding: 8,
                    tickValues,
                    format: v => `${Math.round(Number(v))}`,
                }}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                enableGridY={false}
                gridXValues={tickValues}
                legends={[
                    {
                        dataFrom: 'keys',
                        anchor: 'top-left',
                        direction: 'row',
                        translateY: -18,
                        itemWidth: 100,
                        itemHeight: 16,
                        symbolSize: 10,
                    },
                ]}
                theme={chartTheme}
            />
        </Box>
    );
};
